import {toolState, rightCanvasData, getCanvas} from "@/modules/reader/tools/tools";
import {saveTmpCanvasData, restoreTmpCanvasData, getTouchOffset, setCanvasClientPos, checkIsOutOfCanvas} from "@/modules/utils/drawUtils";
import {updateRgbaStrOpacity, rgbaArr2Str, argbHex2rgbaArr} from "@/modules/utils/colorUtils";
import {isMultiTouch, replaceDefaultTouchBehavior, restoreDefaultTouchBehavior, setBodyPinchZoomOnly} from "@/modules/utils/touchUtils";
import config from "@/config/config.json";
import {useTool} from "@/modules/reader/tools/tools"
import {usePaging} from "@/modules/reader/book/paging";

let canvasElList = {}; //canvas element list
let canvasEl; //current active canvas element
let ctx; //current active canvas context
let touchStartViewportScale;
let touchEndViewportScale;
let history = [];

const {pageState} = usePaging();

const init = (el) =>{
    canvasElList[el.id] = el;
    //apply alpha false to boost performance, especially on iPad safari!
}


const activate = (id)=> {

    if (getCanvas(id)) {
        canvasEl = getCanvas(id);
        setCanvasClientPos(canvasEl);
        canvasEl.addEventListener("mousedown", onMouseDown);
        canvasEl.addEventListener("touchstart", onMouseDown);
        canvasEl.addEventListener("mouseup", onMouseUp);
        canvasEl.addEventListener("touchend", onMouseUp);
    }
}

const deactivate = (id)=> {
    if (getCanvas(id)) {
        canvasEl = getCanvas(id);
        canvasEl.removeEventListener("mousedown", onMouseDown);
        canvasEl.removeEventListener("touchstart", onMouseDown);
        canvasEl.removeEventListener("mouseup", onMouseUp);
        canvasEl.removeEventListener("touchend", onMouseUp);
    }
}

const onMouseDown = (e)=> {

    touchStartViewportScale = window.visualViewport.scale;

    if (isMultiTouch(e)) {
        return;
    }

    e.stopPropagation();
    toolState.isUsing = true;
    canvasEl = e.target;

    //for getting canvas left top position on touch device,
    // since toggling page mode will change the canvas position, so update it again on touchstart
    setCanvasClientPos(canvasEl);

    let offsetValue =  getTouchOffset(e);
    let x = offsetValue.x;
    let y = offsetValue.y;

    useTool().closeToolBoxAndPanel();
    ctx = canvasEl.getContext("2d");
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.lineWidth =  toolState.eraserSetting.size * config.reader.maxZoom;
    ctx.setLineDash([]);
    ctx.strokeStyle = "rgba(0,0,0,1)";
    ctx.beginPath();
    ctx.globalCompositeOperation = "destination-out";
    ctx.moveTo(x,y);
    //history.push({x:x / config.reader.maxZoom ,y: y / config.reader.maxZoom});

    useTool().addHistory(history, x * 2, y * 2);
    canvasEl.addEventListener("mousemove", onMouseMove);
    canvasEl.addEventListener("touchmove", onMouseMove);
    canvasEl.addEventListener("mouseout", onMouseOut);
    canvasEl.addEventListener("touchcancel", onMouseOut);
    canvasEl.addEventListener("mouseup", onMouseUp);
    canvasEl.addEventListener("touchend", onMouseUp);

}

const onMouseUp  = (e) => {

    touchEndViewportScale = window.visualViewport.scale;

    e.stopPropagation();
    canvasEl.removeEventListener("mousemove", onMouseMove);
    canvasEl.removeEventListener("touchmove", onMouseMove);
    canvasEl.removeEventListener("mouseout", onMouseOut);
    canvasEl.removeEventListener("touchcancel", onMouseOut);
    canvasEl.removeEventListener("mouseup", onMouseUp);
    canvasEl.removeEventListener("touchend", onMouseUp);

    if (isMultiTouch(e) || touchStartViewportScale !== touchEndViewportScale) {
        toolState.isUsing = false;
        return;
    }


    //The digit after the last hyphen is the page index (zero based). Use this number to clone image for switching page mode
    let targetPageNum = parseInt(canvasEl.id.split("-").pop());
    let offsetValue =  getTouchOffset(e);
    let x = offsetValue.x;
    let y = offsetValue.y;

    ctx.lineTo(x,y);
    ctx.closePath();
    ctx.globalCompositeOperation = "source-over";
    useTool().saveDrawing(targetPageNum, ctx, history);
    history = [];

    //canvas element id format looks like: tool-canvas-left-0 or tool-canvas-right-1.
    if (targetPageNum % 2 === 1) {
        canvasEl.toBlob((blob) => {
            rightCanvasData.value[targetPageNum] = blob;
            toolState.isRightPageUpdated = true;
        });
    }

    setBodyPinchZoomOnly();
    toolState.isUsing = false;

}

//TODO: move to common method, don't repeat!
const onMouseOut = (e) => {
    checkIsOutOfCanvas(e, onMouseUp);
}


const onMouseMove = (e) => {

    if (isMultiTouch(e)) {
        return;
    }

    e.stopPropagation();
    let offsetValue =  getTouchOffset(e);
    let x = offsetValue.x;
    let y = offsetValue.y;

    ctx.lineTo(x,y);
    ctx.stroke();
    useTool().addHistory(history, x * 2, y * 2);
}

//TODO: clean dirties..
const eraseAll = async () => {

    if (!pageState.isRightPageAvailable) {
        let currentCanvas = getCanvas(`tool-canvas-left-${pageState.currentPage[0]}`);
        let ctx = currentCanvas.getContext("2d");
        ctx.clearRect(0, 0,  ctx.canvas.width, ctx.canvas.height);

        if (pageState.currentPage[0] % 2 === 1) {

            let canvasRight = getCanvas(`tool-canvas-right-${pageState.currentPage[0]}`);
            let ctxRight = canvasRight.getContext("2d");
            ctxRight.clearRect(0, 0, ctxRight.canvas.width, ctxRight.canvas.height);
        }

        useTool().saveDrawing(pageState.currentPage[0], ctx, []);
        history = [];

    } else {

        let canvasLeftFirstPage = getCanvas(`tool-canvas-left-${pageState.currentPage[0]}`);
        let ctxLeftFirstPage = canvasLeftFirstPage.getContext("2d");
        ctxLeftFirstPage.clearRect(0, 0,  ctxLeftFirstPage.canvas.width, ctxLeftFirstPage.canvas.height);

        let canvasLeftSecondPage = getCanvas(`tool-canvas-left-${pageState.currentPage[1]}`);
        let ctxLeftSecondPage = canvasLeftSecondPage.getContext("2d");
        ctxLeftSecondPage.clearRect(0, 0,  ctxLeftSecondPage.canvas.width, ctxLeftSecondPage.canvas.height);

        let canvasRight = getCanvas(`tool-canvas-right-${pageState.currentPage[1]}`);
        let ctxRight = canvasRight.getContext("2d");
        ctxRight.clearRect(0, 0, ctxRight.canvas.width, ctxRight.canvas.height);

        await useTool().saveDrawing(pageState.currentPage[0], ctxLeftFirstPage, []);
        await useTool().saveDrawing(pageState.currentPage[1], ctxRight, []);

        history = [];
    }
}

const restoreHistory = (ctx, data) =>{

    let rgba = argbHex2rgbaArr(data.color);

    console.log("eraser restore")

    ctx.globalCompositeOperation = "destination-out";
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.lineWidth =  data.size * config.reader.maxZoom;
    ctx.setLineDash([]);
    ctx.strokeStyle = "rgba(0,0,0,1)";
    ctx.beginPath();

    for (let point of data.points) {
        ctx.lineTo(point.x * config.reader.maxZoom / 2 , point.y * config.reader.maxZoom / 2);
    }
    ctx.stroke();
    ctx.closePath();
    ctx.globalCompositeOperation = "source-over";

}

export{
    init,
    activate,
    deactivate,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    restoreHistory,
    eraseAll
}